import Phaser from 'phaser';
import { ROOMS, NPCS, OBJECTS, WORLD_CONFIG, DIALOGUE } from '../world.config.js';
import { DroneController } from '../systems/DroneController.js';
import { DialogueSystem, hasVisited } from '../systems/DialogueSystem.js';
import { ModalSystem } from '../systems/ModalSystem.js';
import { ObjectManager } from '../systems/ObjectManager.js';
import { DockSystem } from '../systems/DockSystem.js';
import { TVPortfolioSystem } from '../systems/TVPortfolioSystem.js';
import { EmailCaptureSystem } from '../systems/EmailCaptureSystem.js';
import { trackEvent } from '../lib/events.js';
// v1: RoomManager and Minimap are unused (single-room studio). They remain
// in src/ as-is for v2+ revival when the multi-room build comes back. See
// V1-STUDIO-SCOPE.md "North Star / Backlog".

const FADE_MS = 280;

// Trailer mode — `?trailer=1` URL param skips intro + dialogue, hides all
// HUD chrome, and runs a scripted camera sweep through the studio so the
// page can be screen-recorded as a promo MP4.
function isTrailerMode() {
  return new URLSearchParams(window.location.search).get('trailer') === '1';
}

export class HouseScene extends Phaser.Scene {
  constructor() {
    super('HouseScene');
  }

  create() {
    this.tile = WORLD_CONFIG.tileSize * WORLD_CONFIG.scale;
    this.roomContainer = null;
    this.uiContainer = null;

    this.dialogue = new DialogueSystem();
    this.modal = new ModalSystem();
    this.emailCapture = new EmailCaptureSystem();
    this.dock = new DockSystem({
      onOpen: () => {
        this.controller?.setEnabled(false);
        this.objectManager?.setSuspended(true);
      },
      onClose: () => {
        this.controller?.setEnabled(true);
        this.objectManager?.setSuspended(false);
      },
    });

    this.cameras.main.setBackgroundColor('#0d0a08');

    // Drone is created once and persists across rooms.
    // Scale via _displayScaleFor so the drone sprite (32-px source) matches
    // the new world tile size automatically.
    this.drone = this.add.image(0, 0, 'drone-default').setDepth(10);
    const droneScale = this._displayScaleFor('drone-default');
    this.drone.setScale(droneScale);
    // Subtle hover pulse — sells "rotors spinning, hovering in place"
    // without affecting position. Doesn't conflict with movement input.
    this.tweens.add({
      targets: this.drone,
      scaleX: droneScale * 1.04,
      scaleY: droneScale * 1.04,
      duration: 1100,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    this.controller = new DroneController(this, this.drone);

    this.tvPortfolio = new TVPortfolioSystem({
      onOpen: () => {
        this.controller?.setEnabled(false);
        this.objectManager?.setSuspended(true);
        this.dock?.setSuspended(true);
      },
      onClose: () => {
        this.controller?.setEnabled(true);
        this.objectManager?.setSuspended(false);
        this.dock?.setSuspended(false);
      },
    });

    const startRoomId = WORLD_CONFIG.defaultRoom;
    this.objectManager = new ObjectManager(this, this.drone, startRoomId, {
      onModal: (obj) => this.openModal(obj),
      onLink: (obj) => this.openLink(obj),
      onDialogue: (obj) => this.openDialogue(obj),
      onTvPortfolio: () => this.openTvPortfolio(),
      onMail: () => this.dock?.openMail(),
    });

    this.buildRoom(startRoomId, null);
    this.maybePlayFlyInOrIntro(startRoomId);

    this.scale.on('resize', this._alignChromeToRoom, this);
    this.events.once('shutdown', () => {
      this.scale.off('resize', this._alignChromeToRoom, this);
    });
  }

  // First-time visitors: drone fades in just outside the studio's south
  // door and tweens north into the room, then dialogue fires. Repeat
  // visitors skip straight to the intro (or no intro at all if dialogue
  // has already played). Replaces the old pool-deck cinematic per
  // V1-STUDIO-SCOPE.md "Drone fly-in (lite)".
  maybePlayFlyInOrIntro(roomId) {
    const room = ROOMS.find((r) => r.id === roomId);
    // Trailer mode wins over everything — no fly-in, no dialogue, just the
    // scripted camera sweep.
    if (room && isTrailerMode()) {
      this._runTrailer(room);
      return;
    }
    if (!room || hasVisited()) {
      // Repeat visitor — spawn in place, no animation
      this.maybePlayIntro(roomId);
      return;
    }

    const tile = this.tile;
    const spawnX = room.spawnX * tile + tile / 2;
    const spawnY = room.spawnY * tile + tile / 2;
    // Start position: just south of the studio's south wall — off-screen,
    // as if the drone flew up from the front step.
    const startX = spawnX;
    const startY = (room.heightTiles + 3) * tile;

    this.controller.setEnabled(false);
    this.objectManager.setSuspended(true);
    this.dock?.setSuspended(true);

    this.drone.setPosition(startX, startY);
    this.drone.setAlpha(0);

    // Fly-in: ~2 seconds total. Fade in, drift north into the studio, settle.
    this.tweens.add({ targets: this.drone, alpha: 1, duration: 400, ease: 'Sine.easeIn' });
    this.tweens.add({
      targets: this.drone,
      x: spawnX,
      y: spawnY,
      duration: 1600,
      ease: 'Sine.easeInOut',
      onComplete: () => this.maybePlayIntro(roomId),
    });
  }

  // Trailer mode — scripted cinematic camera sweep through the studio.
  // Hides all UI chrome, adds letterbox bars + opening/closing title cards,
  // varies camera zoom for dramatic emphasis, and auto-opens the laptop
  // modal mid-sweep to demonstrate interactivity. ~11 seconds total so it
  // screen-records cleanly into a short promo MP4. URL: `?trailer=1`.
  _runTrailer(room) {
    const tile = this.tile;
    const cam = this.cameras.main;
    const px = (tx, ty) => ({ x: tx * tile + tile / 2, y: ty * tile + tile / 2 });

    // Hide every interactive surface so the recording is pure cinematic
    this.controller.setEnabled(false);
    this.objectManager.setSuspended(true);
    this.dock?.setSuspended(true);
    // Hide the entire HUD container — room label, controls hint, anything
    // else attached. setVisible(false) survives the alpha tweens that
    // _drawHud queues up, which setAlpha(0) does not.
    this.uiContainer?.setVisible(false);
    document.querySelector('.iw-prompt')?.style.setProperty('display', 'none', 'important');
    document.querySelector('.iw-dock')?.style.setProperty('display', 'none', 'important');
    // Sprout Lands attribution lives in index.html as a fixed DOM element
    document.querySelector('.iw-credit')?.style.setProperty('display', 'none', 'important');
    document.body.style.cursor = 'none';

    // ─── Letterbox bars (Tier 1) ──────────────────────────────────────
    // Black bars top and bottom, fixed to camera, fade in to set the
    // "this is a film, not a screen recording" tone.
    const barH = 64;
    const topBar = this.add.rectangle(cam.width / 2, barH / 2, cam.width, barH, 0x000000, 1)
      .setScrollFactor(0).setDepth(100).setAlpha(0);
    const bottomBar = this.add.rectangle(cam.width / 2, cam.height - barH / 2, cam.width, barH, 0x000000, 1)
      .setScrollFactor(0).setDepth(100).setAlpha(0);
    this.tweens.add({ targets: [topBar, bottomBar], alpha: 1, duration: 400, ease: 'Sine.easeIn' });

    // ─── Opening title card (Tier 1) ──────────────────────────────────
    const titleStart = this.add.text(cam.width / 2, cam.height / 2, "Ian's World", {
      fontFamily: 'Georgia, "Times New Roman", serif',
      fontSize: '56px',
      color: '#f4ead5',
      fontStyle: 'bold',
    }).setOrigin(0.5).setScrollFactor(0).setDepth(101).setAlpha(0);
    this.tweens.add({
      targets: titleStart, alpha: 1, duration: 500, delay: 250,
      ease: 'Sine.easeIn', yoyo: true, hold: 900,
      onComplete: () => titleStart.destroy(),
    });

    // ─── Camera setup ─────────────────────────────────────────────────
    // Start at zoom 1.0 (whole room visible) and start following the drone.
    // When we zoom past 1.0 the camera follow will keep the drone framed.
    cam.setZoom(1.0);
    cam.startFollow(this.drone, true, 0.08, 0.08);

    // Drone fades in below the door
    const door = px(room.spawnX, room.heightTiles + 2);
    const entry = px(room.spawnX, room.spawnY);
    this.drone.setPosition(door.x, door.y);
    this.drone.setAlpha(0);
    this.tweens.add({ targets: this.drone, alpha: 1, duration: 350, delay: 600, ease: 'Sine.easeIn' });

    // ─── Phase scheduler ──────────────────────────────────────────────
    // Each phase is dispatched by absolute delay (ms) from trailer start.
    // Drone and camera tweens that overlap (e.g. zoom + sweep) share a
    // start time and duration so they finish together.
    let t = 600;

    // Phase 1 — drift up from door to spawn (800ms, zoom 1.0)
    this.tweens.add({
      targets: this.drone, x: entry.x, y: entry.y,
      duration: 800, ease: 'Sine.easeInOut', delay: t,
    });
    t += 800;

    // Phase 2 — zoom in to 1.6x while sweeping to the worktable (1500ms).
    // Drone hovers directly in front of (south of) pixel-Ian — like a
    // visitor approaching to talk. NPC lives at (12, 7); drone at (12, 9)
    // puts him cleanly 2 tiles below. Composition stacks vertically:
    // worktable + laptop on top, NPC mid, drone just below NPC.
    const worktablePos = px(12, 9);
    this.tweens.add({ targets: cam, zoom: 1.6, duration: 1500, ease: 'Sine.easeInOut', delay: t });
    this.tweens.add({
      targets: this.drone, x: worktablePos.x, y: worktablePos.y,
      duration: 1500, ease: 'Sine.easeInOut', delay: t,
    });
    t += 1500;

    // Phase 3 — NPC cliffhanger dialogue (Tier 2). Drone has arrived at the
    // worktable. Trigger just the first two lines of pixel-Ian's intro as a
    // tease of what first-time visitors actually experience.
    const cliffhangerLines = [
      { speaker: 'IAN', text: "Oh hey." },
      { speaker: 'IAN', text: "You found me." },
    ];
    this.time.delayedCall(t, () => {
      this.dialogue.open(cliffhangerLines, {});
    });
    // Advance from line 1 to line 2 after the first line has had time to type
    this.time.delayedCall(t + 1100, () => this.dialogue._advance?.());
    // Close after both lines have typed out and held
    this.time.delayedCall(t + 2700, () => this.dialogue.close?.());
    t += 3000;

    // Phase 4 — zoom out to 1.2x + sweep to the Eames chair (1300ms)
    const eamesPos = px(5, 6);
    this.tweens.add({ targets: cam, zoom: 1.2, duration: 1300, ease: 'Sine.easeInOut', delay: t });
    this.tweens.add({
      targets: this.drone, x: eamesPos.x, y: eamesPos.y,
      duration: 1300, ease: 'Sine.easeInOut', delay: t,
    });
    t += 1300;

    // Phase 5 — pan diagonally up-right to the neighbor photo (1100ms)
    const neighborPos = px(20, 2);
    this.tweens.add({
      targets: this.drone, x: neighborPos.x, y: neighborPos.y,
      duration: 1100, ease: 'Sine.easeInOut', delay: t,
    });
    t += 1100;

    // Phase 6 — drop down-right to the TV (900ms)
    const tvPos = px(22, 8);
    this.tweens.add({
      targets: this.drone, x: tvPos.x, y: tvPos.y,
      duration: 900, ease: 'Sine.easeInOut', delay: t,
    });
    t += 900;

    // Phase 7 — sweep down-left to the ball basket (1000ms)
    const basketPos = px(16, 13);
    this.tweens.add({
      targets: this.drone, x: basketPos.x, y: basketPos.y,
      duration: 1000, ease: 'Sine.easeInOut', delay: t,
    });
    t += 1000;

    // Phase 8 — pull back to zoom 1.0 + settle at center (1300ms)
    const centerPos = px(12, 9);
    this.tweens.add({ targets: cam, zoom: 1.0, duration: 1300, ease: 'Sine.easeInOut', delay: t });
    this.tweens.add({
      targets: this.drone, x: centerPos.x, y: centerPos.y,
      duration: 1300, ease: 'Sine.easeInOut', delay: t,
    });
    t += 1300;

    // Phase 9 — fade the studio to black, then reveal "ianstandbridge.com"
    // on the black background. Cleaner than overlaying text on the studio.
    const fadeOverlay = this.add.rectangle(cam.width / 2, cam.height / 2, cam.width, cam.height, 0x000000, 1)
      .setScrollFactor(0).setDepth(105).setAlpha(0);
    this.tweens.add({
      targets: fadeOverlay, alpha: 1, duration: 800, ease: 'Sine.easeIn', delay: t,
    });
    t += 800;

    const titleEnd = this.add.text(cam.width / 2, cam.height / 2, "ianstandbridge.com", {
      fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
      fontSize: '34px',
      color: '#f4ead5',
      letterSpacing: 4,
    }).setOrigin(0.5).setScrollFactor(0).setDepth(110).setAlpha(0);
    this.tweens.add({
      targets: titleEnd, alpha: 1, duration: 500, delay: t,
      ease: 'Sine.easeIn', hold: 1800, yoyo: true,
    });
  }

  // Room lifecycle ───────────────────────────────────────────────────────

  buildRoom(roomId, fromRoomId) {
    const room = ROOMS.find((r) => r.id === roomId);
    if (!room) return;
    this.currentRoom = room;
    const tile = this.tile;
    this.worldWidth = room.widthTiles * tile;
    this.worldHeight = room.heightTiles * tile;

    // Image-backed rooms (Path D rendering) override programmatic floor +
    // walls + furniture. Fall back to programmatic sprite rendering if the
    // background image isn't loaded yet (graceful dev experience while
    // backgrounds are being authored room-by-room).
    const sceneKey = `scene-${roomId}`;
    this.usingImageBackground = !!(
      room.backgroundImage && this.textures.exists(sceneKey)
        && this.textures.get(sceneKey).key !== '__MISSING'
    );

    this.roomContainer = this.add.container(0, 0).setDepth(0);

    if (this.usingImageBackground) {
      this._drawImageBackground(room, sceneKey, tile);
      // Studio shell: AI render is architecture only (walls, floor, window).
      // Engine adds the door + lamp glows + vignette on top so they stay
      // aligned with gameplay (door is interactive in spirit; glows track
      // the floor lamp object's position; vignette adds cozy mood).
      // The AI shell intentionally does NOT include a door — including one
      // pulls the model into front-facing perspective. See
      // V1-STUDIO-SCOPE.md "Studio shell prompt (V3)" for the rationale.
      if (room.shellMode && room.id === 'studio') {
        this._drawStudioDoor(room, tile);
      }
      this._drawDoors(room, tile);
      // Ambient glows render BEFORE objects so warm pools sit under sprites.
      if (room.shellMode) this._drawAmbientGlows(room, tile);
      this._drawObjects(room, tile);
      this._drawNpcs(room, tile);
      if (room.shellMode && room.id === 'studio') {
        this._drawStudioVignette(room, tile);
      }
    } else {
      this._drawFloor(room, tile);
      // v1 studio: paint a warm sunset clerestory strip along the inside
      // of the north wall (the "Lake Tahoe alpine sunset" window vibe per
      // V1-STUDIO-SCOPE.md), a visual entry door on the south wall, plus
      // a soft warm vignette over everything to add coziness.
      if (room.id === 'studio') {
        this._drawStudioWindow(room, tile);
        this._drawStudioDoor(room, tile);
      }
      this._drawDoors(room, tile);
      this._drawAmbientGlows(room, tile);
      this._drawObjects(room, tile);
      this._drawNpcs(room, tile);
      if (room.id === 'studio') {
        this._drawStudioVignette(room, tile);
      }
    }

    // v1: single-room — always use the room's spawn point. The fromRoomId
    // entry-resolution lives in RoomManager.entryFor for v2+ revival.
    const entry = { x: room.spawnX, y: room.spawnY };
    this.drone.setPosition(entry.x * tile + tile / 2, entry.y * tile + tile / 2);

    this._setupCamera();

    this._drawHud(room);

    this.objectManager.setRoom(roomId);

    this._alignChromeToRoom();

    this.cameras.main.fadeIn(FADE_MS, 0, 0, 0);
  }

  // Convert the room's bottom-edge canvas y into viewport pixels and tell the dock
  // to dock itself just under it. Re-runs on every resize via the listener in create().
  _alignChromeToRoom() {
    if (!this.dock) return;
    const cam = this.cameras.main;
    const canvas = this.sys.game.canvas;
    const rect = canvas.getBoundingClientRect();
    const scaleY = rect.height / cam.height;
    const roomBottomCanvas = Math.min(
      (cam.height - this.worldHeight) / 2 + this.worldHeight,
      cam.height,
    );
    const roomBottomViewport = rect.top + roomBottomCanvas * scaleY;
    this.dock.alignToRoomBottom(roomBottomViewport);
  }

  teardownRoom() {
    this.roomContainer?.destroy(true);
    this.roomContainer = null;
    this.uiContainer?.destroy(true);
    this.uiContainer = null;
  }

  // Per-room camera setup. For each axis: if room is larger than viewport,
  // follow the drone with bounds; if smaller, center the room on that axis.
  _setupCamera() {
    const cam = this.cameras.main;
    const vw = cam.width;
    const vh = cam.height;
    const ww = this.worldWidth;
    const wh = this.worldHeight;
    const lerp = WORLD_CONFIG.cameraLerp;

    cam.stopFollow();

    const followX = ww > vw;
    const followY = wh > vh;

    if (followX || followY) {
      // Set generous bounds so follow can clamp on the larger axis without snapping the smaller axis to 0
      cam.setBounds(
        followX ? 0 : -(vw - ww) / 2,
        followY ? 0 : -(vh - wh) / 2,
        followX ? ww : vw,
        followY ? wh : vh,
      );
      cam.startFollow(this.drone, true, lerp, lerp);
    } else {
      // Whole room fits in the viewport — pin the camera so the room is centered
      cam.removeBounds?.();
      cam.setScroll(-(vw - ww) / 2, -(vh - wh) / 2);
    }
  }

  // v1: no room transitions (single studio). transitionTo lives on in
  // git history + world.config.future.js — when v2+ revives multi-room,
  // copy the original implementation back from there.

  // Drawing ──────────────────────────────────────────────────────────────

  // Draw the AI-rendered room image as the room's background, sized to fill
  // the room's world rect. Path D rendering — see HOUSE-DESIGN-BRIEF.md
  // "Rendering architecture (Path D)".
  _drawImageBackground(room, sceneKey, tile) {
    const ww = room.widthTiles * tile;
    const wh = room.heightTiles * tile;
    const bg = this.add.image(0, 0, sceneKey)
      .setOrigin(0, 0)
      .setDepth(0);
    // Stretch the image to fit the room's world rect. Per-room aspect ratio
    // can be tuned by setting the room's widthTiles/heightTiles to match
    // the image's aspect.
    bg.setDisplaySize(ww, wh);
    this.roomContainer.add(bg);
  }

  _drawFloor(room, tile) {
    const { id: roomId } = room;
    if (roomId === 'backyard') {
      this._drawOutdoorFloor(room, tile);
    } else if (roomId === 'pool-deck') {
      this._drawPoolDeckFloor(room, tile);
    } else {
      this._drawInteriorFloor(room, tile);
    }
    this._drawWalls(room, tile);
    if (roomId === 'pool-deck') {
      this._drawPoolDeckFeatures(room, tile);
    }
  }

  // Wood deck floor for the pool deck — same wood plank pattern as interior
  // floors but darker plank tone (weather-treated), and the pool/hot-tub
  // overlays get drawn separately by _drawPoolDeckFeatures.
  _drawPoolDeckFloor(room, tile) {
    const { widthTiles, heightTiles } = room;
    const g = this.add.graphics();
    const PLANK = 0x9b6b3a;       // weathered walnut
    const PLANK_LIGHT = 0xb88454;
    const PLANK_DARK = 0x7a4f28;
    const SEAM = 0x4a2f1c;

    for (let y = 1; y < heightTiles - 1; y++) {
      for (let x = 1; x < widthTiles - 1; x++) {
        const plankIdx = Math.floor(x / 2);
        const base = plankIdx % 2 === 0 ? PLANK : PLANK_DARK;
        g.fillStyle(base, 1);
        g.fillRect(x * tile, y * tile, tile, tile);
        // Long-grain plank seam (vertical, since pool deck planks run lake-to-house)
        g.fillStyle(SEAM, 0.6);
        g.fillRect(x * tile, y * tile, 1, tile);
        // Subtle highlight every few tiles
        if ((x + y) % 5 === 0) {
          g.fillStyle(PLANK_LIGHT, 0.35);
          g.fillRect(x * tile + 2, y * tile + Math.floor(tile / 3), tile - 4, 1);
        }
      }
    }
    this.roomContainer.add(g);
  }

  // Programmatic placeholder for the two-level pool, hot tub, and waterfall
  // on the pool deck. Replaced once we have the interior reference from
  // ROUND-3-AUDITION-INTERIORS.md and proper hero-object sprites.
  _drawPoolDeckFeatures(room, tile) {
    const g = this.add.graphics();

    // Pool palette (deeper / richer than v1 — reads like real water)
    const WATER_DEEP = 0x2e5a78;
    const WATER_MID = 0x4d8aac;
    const WATER_LIGHT = 0x82b0c8;
    const WATER_SHIMMER = 0xc4dceb;
    const STONE_RIM = 0xDCD0B8;          // light cream sandstone (matches brief)
    const STONE_RIM_LIGHT = 0xEDE4D0;
    const STONE_RIM_SHADOW = 0xB8AC92;
    const STONE_DEEP = 0x8a7e6b;
    const HOT_TUB_WATER = 0x4a6b78;
    const HOT_TUB_GLOW = 0xf6b93b;

    // Upper pool — 8 tiles wide × 3 tiles deep, organic kidney-ish shape
    const px = 5 * tile;
    const py = 5 * tile;
    const pw = 8 * tile;
    const ph = 3 * tile;

    // Outer stone rim shadow
    g.fillStyle(STONE_RIM_SHADOW, 1);
    g.fillRoundedRect(px - 6, py - 4, pw + 12, ph + 12, 14);
    // Stone rim
    g.fillStyle(STONE_RIM, 1);
    g.fillRoundedRect(px - 6, py - 6, pw + 12, ph + 12, 14);
    // Rim top highlight
    g.fillStyle(STONE_RIM_LIGHT, 0.7);
    g.fillRoundedRect(px - 6, py - 6, pw + 12, 2, 14);

    // Water body (slightly inset from rim)
    g.fillStyle(WATER_DEEP, 1);
    g.fillRoundedRect(px, py, pw, ph, 10);
    g.fillStyle(WATER_MID, 1);
    g.fillRoundedRect(px, py, pw, ph - 4, 10);

    // Lap-lane indicator (subtle, in the deeper west portion)
    g.fillStyle(WATER_LIGHT, 0.35);
    g.fillRect(px + 16, py + ph / 2 - 1, pw / 2 - 24, 2);

    // Surface shimmer dots
    g.fillStyle(WATER_SHIMMER, 0.55);
    for (let i = 0; i < 12; i++) {
      const sx = px + 16 + ((i * 31) % (pw - 32));
      const sy = py + 8 + ((i * 17) % (ph - 16));
      g.fillRect(sx, sy, 4, 1);
      g.fillRect(sx + 1, sy + 1, 2, 1);
    }

    // Waterfall — stone lip on the south side of the upper pool
    const wfY = py + ph;
    g.fillStyle(STONE_DEEP, 1);
    g.fillRect(px + 32, wfY, pw - 64, 6);
    g.fillStyle(STONE_RIM_SHADOW, 1);
    g.fillRect(px + 32, wfY + 4, pw - 64, 2);
    // Falling water
    g.fillStyle(WATER_SHIMMER, 0.85);
    g.fillRect(px + 36, wfY + 6, pw - 72, 14);
    g.fillStyle(WATER_LIGHT, 0.7);
    g.fillRect(px + 36, wfY + 6, pw - 72, 4);
    // Waterfall spray dots at the bottom
    g.fillStyle(WATER_SHIMMER, 0.5);
    for (let i = 0; i < 6; i++) {
      g.fillRect(px + 40 + i * 14, wfY + 20, 2, 1);
    }

    // Lower pool — smaller, beneath the waterfall, more organic
    const lpX = px + 24;
    const lpY = wfY + 24;
    const lpW = pw - 48;
    const lpH = Math.round(1.5 * tile);

    g.fillStyle(STONE_RIM_SHADOW, 1);
    g.fillRoundedRect(lpX - 5, lpY - 3, lpW + 10, lpH + 10, 14);
    g.fillStyle(STONE_RIM, 1);
    g.fillRoundedRect(lpX - 5, lpY - 5, lpW + 10, lpH + 10, 14);
    g.fillStyle(STONE_RIM_LIGHT, 0.6);
    g.fillRoundedRect(lpX - 5, lpY - 5, lpW + 10, 2, 14);

    g.fillStyle(WATER_DEEP, 1);
    g.fillRoundedRect(lpX, lpY, lpW, lpH, 10);
    g.fillStyle(WATER_MID, 1);
    g.fillRoundedRect(lpX, lpY, lpW, lpH - 3, 10);

    g.fillStyle(WATER_SHIMMER, 0.5);
    for (let i = 0; i < 5; i++) {
      g.fillRect(lpX + 12 + i * 16, lpY + 8 + (i % 2) * 4, 3, 1);
    }

    // Granite rocks framing the lower pool's south side (decorative)
    g.fillStyle(0x6b7470, 1);
    g.fillCircle(lpX + 8, lpY + lpH + 2, 8);
    g.fillCircle(lpX + lpW - 10, lpY + lpH + 4, 7);
    g.fillCircle(lpX + lpW / 2, lpY + lpH + 6, 9);
    g.fillStyle(0x9da89a, 0.7); // highlight
    g.fillRect(lpX + 4, lpY + lpH - 2, 3, 1);
    g.fillRect(lpX + lpW / 2 - 3, lpY + lpH + 1, 4, 1);

    // Hot tub — separate small square east of the upper pool
    const tubX = px + pw + 24;
    const tubY = py + 12;
    const tubSize = Math.round(1.5 * tile);
    // Cream rim
    g.fillStyle(STONE_RIM_SHADOW, 1);
    g.fillRect(tubX - 4, tubY - 2, tubSize + 8, tubSize + 8);
    g.fillStyle(STONE_RIM, 1);
    g.fillRect(tubX - 4, tubY - 4, tubSize + 8, tubSize + 8);
    g.fillStyle(STONE_RIM_LIGHT, 0.7);
    g.fillRect(tubX - 4, tubY - 4, tubSize + 8, 2);
    // Hot tub water
    g.fillStyle(HOT_TUB_WATER, 1);
    g.fillRect(tubX, tubY, tubSize, tubSize);
    g.fillStyle(WATER_MID, 0.7);
    g.fillRect(tubX, tubY, tubSize, tubSize - 4);
    // Amber underglow
    g.fillStyle(HOT_TUB_GLOW, 0.55);
    g.fillRect(tubX - 4, tubY + tubSize + 4, tubSize + 8, 2);
    g.fillStyle(HOT_TUB_GLOW, 0.3);
    g.fillRect(tubX - 6, tubY + tubSize + 6, tubSize + 12, 2);
    // Steam wisps (3)
    g.fillStyle(0xffffff, 0.6);
    g.fillRect(tubX + tubSize / 4, tubY - 12, 1, 6);
    g.fillRect(tubX + tubSize / 4 + 1, tubY - 16, 1, 4);
    g.fillStyle(0xffffff, 0.5);
    g.fillRect(tubX + tubSize / 2, tubY - 14, 1, 5);
    g.fillRect(tubX + tubSize * 3 / 4, tubY - 10, 1, 4);

    this.roomContainer.add(g);
  }

  // Exterior: real Sprout Lands grass + stone path band along the south
  _drawOutdoorFloor(room, tile) {
    const { widthTiles, heightTiles } = room;
    const sidewalkY = heightTiles - 5;
    const innerW = (widthTiles - 2) * tile;
    const grassH = (sidewalkY - 1) * tile;
    const stoneH = (heightTiles - 1 - sidewalkY) * tile;

    // Grass fill across the upper area. With tile=16 base, scale=2, each
    // 16-pixel grass source tile fills exactly one world tile.
    const fillScale = WORLD_CONFIG.scale;
    if (this.textures.exists('tile-grass-fill') && grassH > 0) {
      const grass = this.add.tileSprite(tile, tile, innerW, grassH, 'tile-grass-fill')
        .setOrigin(0, 0)
        .setTileScale(fillScale, fillScale);
      this.roomContainer.add(grass);
    } else {
      // Fallback: programmatic green fill
      const g = this.add.graphics();
      g.fillStyle(0x78a158, 1);
      g.fillRect(tile, tile, innerW, grassH);
      this.roomContainer.add(g);
    }

    // Stone path band along the south
    if (this.textures.exists('tile-stone-fill') && stoneH > 0) {
      const stone = this.add.tileSprite(tile, sidewalkY * tile, innerW, stoneH, 'tile-stone-fill')
        .setOrigin(0, 0)
        .setTileScale(fillScale, fillScale);
      this.roomContainer.add(stone);
    }
  }

  // Interior: programmatic wood-plank floor using Sprout Lands palette colors.
  // Tightened palette in v1-studio so the plank pattern is softer and less
  // visually busy — high contrast was reading as "stripes" instead of wood.
  _drawInteriorFloor(room, tile) {
    const { widthTiles, heightTiles } = room;
    const g = this.add.graphics();

    // Softer wood palette — closer values, gentler contrast
    const WOOD_BASE = 0xB88962;
    const WOOD_LIGHT = 0xC09972;
    const WOOD_DARK = 0xAE805B;
    const WOOD_GRAIN = 0x90625D;

    for (let y = 1; y < heightTiles - 1; y++) {
      for (let x = 1; x < widthTiles - 1; x++) {
        const offset = y % 2 === 0 ? 0 : 1;
        const plankIdx = Math.floor((x + offset) / 3);
        const base = plankIdx % 2 === 0 ? WOOD_BASE : WOOD_DARK;
        g.fillStyle(base, 1);
        g.fillRect(x * tile, y * tile, tile, tile);

        // Plank seam at top of tile (softer)
        g.fillStyle(WOOD_GRAIN, 0.4);
        g.fillRect(x * tile, y * tile, tile, 1);

        // Grain hint (less frequent)
        if ((x + y * 2) % 7 === 0) {
          g.fillStyle(WOOD_GRAIN, 0.25);
          g.fillRect(x * tile + 4, y * tile + Math.floor(tile / 2), tile - 8, 1);
        }

        // Subtle highlight every few tiles (more subtle)
        if ((x * 3 + y * 7) % 17 === 0) {
          g.fillStyle(WOOD_LIGHT, 0.3);
          g.fillRect(x * tile + 2, y * tile + 4, tile - 4, 1);
        }
      }
    }
    this.roomContainer.add(g);
  }

  // Walls — interior uses real wood-wall tile from Sprout Lands; exterior uses programmatic fence.
  // Studio (v1) forces the programmatic path: the Sprout Lands wood-walls tileset reads as
  // a busy purple-toned vertical-stripe pattern that fights the cozy studio vibe — warm
  // walnut programmatic walls work better and match the floor palette.
  _drawWalls(room, tile) {
    const { widthTiles, heightTiles, id: roomId, wallColor } = room;
    const isOutdoor = roomId === 'backyard' || roomId === 'pool-deck';
    const isStudio = roomId === 'studio';
    const useRealWoodWall = !isOutdoor && !isStudio && this.textures.exists('tile-wood-wall');

    if (useRealWoodWall) {
      // Real Sprout Lands wood wall tiles. WORLD_CONFIG.scale matches the
      // 16-px source to one world tile.
      const wallScale = WORLD_CONFIG.scale;
      for (let x = 0; x < widthTiles; x++) {
        for (let y = 0; y < heightTiles; y++) {
          const isWall = x === 0 || y === 0 || x === widthTiles - 1 || y === heightTiles - 1;
          if (!isWall) continue;
          const sprite = this.add.image(x * tile, y * tile, 'tile-wood-wall')
            .setOrigin(0, 0)
            .setScale(wallScale);
          this.roomContainer.add(sprite);
        }
      }
    } else {
      // Programmatic fallback (uses room.wallColor or palette stone color for outdoor)
      const wall = Phaser.Display.Color.IntegerToColor(isOutdoor ? 0x6b7470 : wallColor);
      const wallTopHighlight = wall.clone().lighten(20).color;
      const wallFaceShadow = wall.clone().darken(15).color;
      const g = this.add.graphics();
      for (let x = 0; x < widthTiles; x++) {
        for (let y = 0; y < heightTiles; y++) {
          const isWall = x === 0 || y === 0 || x === widthTiles - 1 || y === heightTiles - 1;
          if (!isWall) continue;
          g.fillStyle(isOutdoor ? 0x6b7470 : wallColor, 1);
          g.fillRect(x * tile, y * tile, tile, tile);
          if (y === 0) {
            g.fillStyle(wallTopHighlight, 1);
            g.fillRect(x * tile, y * tile, tile, 2);
            g.fillStyle(wallFaceShadow, 1);
            g.fillRect(x * tile, y * tile + tile - 3, tile, 3);
          }
          if (y === heightTiles - 1) {
            g.fillStyle(wallTopHighlight, 0.7);
            g.fillRect(x * tile, y * tile, tile, 1);
          }
          if (x === 0 && y > 0 && y < heightTiles - 1) {
            g.fillStyle(wallTopHighlight, 0.5);
            g.fillRect(x * tile + tile - 1, y * tile, 1, tile);
          }
          if (x === widthTiles - 1 && y > 0 && y < heightTiles - 1) {
            g.fillStyle(wallTopHighlight, 0.5);
            g.fillRect(x * tile, y * tile, 1, tile);
          }
        }
      }
      this.roomContainer.add(g);
    }

    // Inside-shadow band — subtle gradient just below the top wall (makes it feel taller)
    if (heightTiles > 2) {
      const shadow = this.add.graphics();
      shadow.fillStyle(0x000000, 0.18);
      shadow.fillRect(tile, tile, (widthTiles - 2) * tile, 3);
      shadow.fillStyle(0x000000, 0.08);
      shadow.fillRect(tile, tile + 3, (widthTiles - 2) * tile, 2);
      this.roomContainer.add(shadow);
    }

    // Wood paneling seams on the top wall — vertical lighter-wood lines
    // every 3 tiles. Makes the wall feel like real wood paneling rather
    // than a flat color block. Studio + other indoor programmatic walls.
    if (!isOutdoor && !useRealWoodWall) {
      const seam = this.add.graphics();
      const seamColor = Phaser.Display.Color.IntegerToColor(wallColor).clone().lighten(10).color;
      seam.fillStyle(seamColor, 0.6);
      for (let x = 3; x < widthTiles; x += 3) {
        // Top wall seam
        seam.fillRect(x * tile, 0, 1, tile);
        // Bottom wall seam
        seam.fillRect(x * tile, (heightTiles - 1) * tile, 1, tile);
      }
      // Side walls — horizontal seams every 4 tiles
      for (let y = 4; y < heightTiles - 1; y += 4) {
        seam.fillRect(0, y * tile, tile, 1);
        seam.fillRect((widthTiles - 1) * tile, y * tile, tile, 1);
      }
      this.roomContainer.add(seam);
    }
  }

  // Studio: paint a visual "door" tile on the south wall at the spawn-x
  // column. There's no transition (single-room v1), but the visual cue
  // reads as "this is where you flew in" and gives the south wall some
  // intentionality. Renders inside the wall border.
  _drawStudioDoor(room, tile) {
    const doorX = room.spawnX;
    const wallY = room.heightTiles - 1;
    const g = this.add.graphics();

    // Door body (warm wood, lighter than the wall)
    g.fillStyle(0x8a5a30, 1);
    g.fillRect(doorX * tile + 2, wallY * tile + 2, tile - 4, tile - 4);
    // Top arch / lintel highlight
    g.fillStyle(0xC9A55A, 0.7);
    g.fillRect(doorX * tile + 2, wallY * tile + 2, tile - 4, 2);
    // Subtle door panel (vertical line center)
    g.fillStyle(0x4A2F1C, 0.5);
    g.fillRect(doorX * tile + tile / 2, wallY * tile + 4, 1, tile - 8);
    // Brass knob
    g.fillStyle(0xC9A55A, 1);
    g.fillRect(doorX * tile + tile - 6, wallY * tile + tile / 2, 2, 2);
    // Soft warm spill from below the door (suggests light from outside)
    g.fillStyle(0xF6B93B, 0.18);
    g.fillRect((doorX - 1) * tile, (wallY - 0.5) * tile, tile * 3, tile);

    this.roomContainer.add(g);
  }

  // Studio: warm sunset clerestory strip along the top of the north wall.
  // Paints a horizontal band of warm peach/orange just above the wall so
  // the room feels like it has a real Lake Tahoe alpine view above eye line.
  _drawStudioWindow(room, tile) {
    const { widthTiles } = room;
    const g = this.add.graphics();

    // The clerestory sits inside the top wall: rows ~0.3 to 0.8 of the
    // first tile row (so it's visible against the wall body, not above it).
    const innerW = (widthTiles - 2) * tile;
    const startX = tile;
    const stripY = Math.round(tile * 0.25);
    const stripH = Math.round(tile * 0.5);

    // Sky gradient (faked with three stacked bands)
    g.fillStyle(0xD9826A, 1); // dusty rose horizon
    g.fillRect(startX, stripY, innerW, stripH);
    g.fillStyle(0xF6B93B, 0.55); // sunset gold mid-band
    g.fillRect(startX, stripY, innerW, Math.round(stripH * 0.6));
    g.fillStyle(0xfde0a8, 0.45); // pale peach top band
    g.fillRect(startX, stripY, innerW, Math.round(stripH * 0.3));

    // Mountain silhouette — three peaks across the strip (very chunky)
    g.fillStyle(0x4C468B, 0.85);
    const peakBaseY = stripY + stripH;
    const peakCount = 5;
    for (let i = 0; i < peakCount; i++) {
      const px = startX + (innerW * i) / peakCount + 12;
      const ph = 4 + ((i * 3) % 5);
      // Triangle approximated with a stack of decreasing-width rects
      for (let r = 0; r < ph; r++) {
        const rw = (ph - r) * 4;
        g.fillRect(px - rw / 2, peakBaseY - r - 1, rw, 1);
      }
    }

    // Mullion bars (vertical thin black lines breaking the strip into panes)
    g.fillStyle(0x14110e, 0.7);
    const paneCount = 6;
    for (let i = 1; i < paneCount; i++) {
      const mx = startX + (innerW * i) / paneCount;
      g.fillRect(mx, stripY, 1, stripH);
    }
    // Top + bottom frame lines for the window
    g.fillStyle(0x14110e, 0.85);
    g.fillRect(startX, stripY - 1, innerW, 1);
    g.fillRect(startX, stripY + stripH, innerW, 1);

    this.roomContainer.add(g);
  }

  // Look at OBJECTS for a `glow` field (color, radius). Paints soft radial
  // light spill on the floor under the object. Used by the floor lamp;
  // any future object can opt in. Renders BEFORE objects so the spill
  // sits under sprites.
  _drawAmbientGlows(room, tile) {
    const g = this.add.graphics();
    g.setDepth(3); // above floor (depth 0-2), below objects (depth 5+)
    let painted = false;

    for (const obj of OBJECTS) {
      if (obj.room !== room.id) continue;
      if (!obj.glow) continue;
      const cx = obj.x * tile + tile / 2;
      const cy = obj.y * tile + tile / 2;
      const color = obj.glow.color ?? 0xF6B93B;
      const radius = obj.glow.radius ?? tile * 2.5;
      // Faked radial gradient: 6 concentric circles with decreasing alpha,
      // brighter near the source so the warm pool actually reads.
      const steps = 6;
      for (let i = steps; i >= 1; i--) {
        const r = (radius * i) / steps;
        const alpha = 0.06 + (0.16 * (steps - i)) / steps;
        g.fillStyle(color, alpha);
        g.fillCircle(cx, cy, r);
      }
      painted = true;
    }

    if (painted) this.roomContainer.add(g);
    else g.destroy();
  }

  // Subtle vignette around the room's interior — corners darken slightly.
  // Renders at depth 8 (above objects but below dialogue/UI). Sells the
  // "cozy interior" mood without obscuring anything.
  _drawStudioVignette(room, tile) {
    const ww = room.widthTiles * tile;
    const wh = room.heightTiles * tile;
    const g = this.add.graphics();
    g.setDepth(8);

    // Four corner radial-darken patches. Tuned ~half the previous strength
    // (peak ~12% black vs the v1 ~28%) so it reads as cozy mood, not as
    // dim/dingy. The AI shell already provides plenty of atmospheric warmth.
    const cornerRadius = tile * 4;
    const corners = [
      [0, 0],          // NW
      [ww, 0],         // NE
      [0, wh],         // SW
      [ww, wh],        // SE
    ];
    for (const [cx, cy] of corners) {
      const steps = 3;
      for (let i = steps; i >= 1; i--) {
        const r = (cornerRadius * i) / steps;
        const alpha = 0.02 + (0.03 * (steps - i)) / steps;
        g.fillStyle(0x000000, alpha);
        g.fillCircle(cx, cy, r);
      }
    }

    this.roomContainer.add(g);
  }

  _drawDoors(room, tile) {
    for (const door of room.doors ?? []) {
      const target = ROOMS.find((r) => r.id === door.toRoom);
      const isLocked = door.locked || target?.locked;

      const tileG = this.add.graphics();
      const baseColor = isLocked ? 0x6b3a3a : 0xf4ead5;
      const baseAlpha = isLocked ? 0.6 : 0.85;
      tileG.fillStyle(baseColor, baseAlpha);
      tileG.fillRect(door.x * tile + 2, door.y * tile + 2, tile - 4, tile - 4);
      this.roomContainer.add(tileG);

      const labelText = isLocked ? `${door.label} · wip` : door.label;
      const labelColor = isLocked ? '#cc8a8a' : '#f4ead5';
      const label = this.add.text(door.x * tile + tile / 2, door.y * tile - 4, labelText, {
        fontFamily: 'monospace',
        fontSize: '10px',
        color: labelColor,
      })
        .setOrigin(0.5, 1)
        .setAlpha(0.85);
      this.roomContainer.add(label);

      if (isLocked) {
        const x = this.add.text(door.x * tile + tile / 2, door.y * tile + tile / 2, '×', {
          fontFamily: 'monospace',
          fontSize: '14px',
          color: '#cc8a8a',
          fontStyle: 'bold',
        })
          .setOrigin(0.5, 0.5);
        this.roomContainer.add(x);
      }
    }
  }

  _drawObjects(room, tile) {
    for (const obj of OBJECTS) {
      if (obj.room !== room.id) continue;
      // For image-backed rooms in *full-painting* mode, objects' visuals
      // come from the AI render and the OBJECT entry exists purely for
      // the interactive hotspot. SHELL mode is different: the AI render
      // is architecture-only (walls/floor/window/door — no furniture)
      // and OBJECTS render on top as visible sprite overlays. Per-object
      // `alwaysRender` continues to force-show in either mode.
      if (this.usingImageBackground && !room.shellMode && !obj.alwaysRender) continue;
      if (!obj.textureKey) continue;
      const px = obj.x * tile + tile / 2;
      // Optional `pixelOffsetY` nudges the sprite up/down within the tile,
      // in scene-pixel units. Used by the worktable laptop to sit on the
      // desk surface (texture row 3-7 of the 14-row worktable) instead of
      // floating at tile-center y (which lands at the desk's front edge).
      let py = obj.y * tile + tile / 2;
      if (obj.pixelOffsetY) py += obj.pixelOffsetY;
      // Optional `depth` override per object (default 5). Surfaces stay at
      // 5; items "on" surfaces (papers, trophies) use 6 to render in front.
      // Floor decoratives (rugs) use 4 to render behind chairs/lamps.
      const depth = obj.depth ?? 5;
      // Optional `targetTileWidth` lets an OBJECT specify how many tiles
      // wide it should display at, regardless of source PNG resolution.
      // Engine computes the scale to hit that target. Lets AI overlay
      // PNGs (which Imagen renders at 1024+ px) auto-fit alongside chunky
      // programmatic placeholders (which are 16-30 px native). Aspect
      // ratio is preserved.
      let baseScale;
      if (obj.targetTileWidth) {
        const tex = this.textures.get(obj.textureKey);
        const srcW = tex?.getSourceImage()?.width ?? 32;
        const targetPx = obj.targetTileWidth * tile;
        baseScale = targetPx / srcW;
      } else {
        baseScale = this._displayScaleFor(obj.textureKey);
      }
      const sprite = this.add.image(px, py, obj.textureKey)
        .setScale(baseScale)
        .setDepth(depth);
      this.roomContainer.add(sprite);
      // Register so ObjectManager can pulse the sprite on proximity.
      this.objectManager?.registerSprite(obj.id, sprite, baseScale);
      // Optional 1-px breathing bob — same pattern as stationary NPCs.
      // Used by the worktable laptop (Claude crab needs to feel alive).
      if (obj.bob) {
        this.tweens.add({
          targets: sprite,
          y: py - 1,
          duration: 1400,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
        });
      }
    }
  }

  // World runs at 16-px tile (locked 2026-05-04). Three sprite tiers:
  //   <32 source  — chunky 14-24px Sprout-Lands-tier sprites (most objects). Scale 2.
  //   32-47       — medium sprites (drone, app icons). Scale 1.
  //   48+         — hero multi-tile sprites (kitchen island, sectional, fireplace).
  //                 Scale 1 — they display at native size and span 2-3 tiles.
  // See HOUSE-DESIGN-BRIEF.md "Pixel-art rendering tier" for the canonical decision.
  _displayScaleFor(textureKey) {
    const tex = this.textures.get(textureKey);
    if (!tex || tex.key === '__MISSING') return WORLD_CONFIG.scale;
    const src = tex.getSourceImage();
    const maxDim = Math.max(src.width ?? 0, src.height ?? 0);
    if (maxDim < 32) return WORLD_CONFIG.scale; // chunky 14-24px → scale 2 → 28-48 display
    return 1;                                   // 32+ source → display at native pixels
  }

  _drawNpcs(room, tile) {
    for (const npc of NPCS) {
      if (npc.room !== room.id) continue;
      const px = npc.x * tile + tile / 2;
      const py = npc.y * tile + tile / 2;
      const textureKey = npc.textureKey ?? 'npc-pixel-ian';
      const sprite = this.add.image(px, py, textureKey)
        .setScale(this._displayScaleFor(textureKey))
        .setDepth(6);
      this.roomContainer.add(sprite);

      if (npc.wander) {
        const w = npc.wander;
        if (w.axis === 'x') {
          const fromPx = w.from * tile + tile / 2;
          const toPx = w.to * tile + tile / 2;
          const distancePx = Math.abs(toPx - fromPx);
          const durationMs = (distancePx / w.speed) * 1000;
          this.tweens.add({
            targets: sprite,
            x: { from: fromPx, to: toPx },
            duration: durationMs,
            yoyo: true,
            repeat: -1,
            ease: 'Sine.easeInOut',
            onYoyo: () => { sprite.setFlipX(true); },
            onRepeat: () => { sprite.setFlipX(false); },
          });
        }
      } else {
        // Stationary NPCs get a subtle 1-pixel breathing bob. Sells "alive"
        // without distracting from the rest of the scene.
        this.tweens.add({
          targets: sprite,
          y: py - 1,
          duration: 1400,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
        });
      }
    }
  }

  _drawHud(room) {
    this.uiContainer = this.add.container(0, 0).setDepth(900).setScrollFactor(0);
    const cam = this.cameras.main;

    // Room edges in canvas coords (clamped so this works for rooms larger than viewport)
    const roomLeftCanvas = Math.max((cam.width - this.worldWidth) / 2, 0);
    const roomTopCanvas = Math.max((cam.height - this.worldHeight) / 2, 0);
    const roomBottomCanvas = Math.min(roomTopCanvas + this.worldHeight, cam.height);

    // Room label — small banner pinned just above the room's top edge, auto-fades
    const label = this.add.text(cam.width / 2, Math.max(roomTopCanvas - 16, 8), room.label.toLowerCase(), {
      fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
      fontSize: '11px',
      color: '#f4ead5',
      letterSpacing: 0.2,
    })
      .setOrigin(0.5, 1)
      .setAlpha(0);
    this.uiContainer.add(label);
    this.tweens.add({ targets: label, alpha: 0.85, duration: 280 });
    this.time.delayedCall(2200, () => {
      this.tweens.add({ targets: label, alpha: 0.3, duration: 600 });
    });

    // Controls hint — pinned just below the room's bottom-left corner.
    // Persistent: visitors should always be able to see the controls so
    // they're never stranded if they missed it during the intro.
    const hint = this.add.text(roomLeftCanvas + 4, Math.min(roomBottomCanvas + 12, cam.height - 14), 'WASD / arrows · E to interact', {
      fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
      fontSize: '10px',
      color: '#f4ead5',
    })
      .setOrigin(0, 0)
      .setAlpha(0);
    this.uiContainer.add(hint);
    this.hintEl = hint;
    this.tweens.add({ targets: hint, alpha: 0.55, duration: 280, delay: 400 });
  }

  // Behavior ─────────────────────────────────────────────────────────────

  maybePlayIntro(roomId) {
    const npc = NPCS.find((n) => n.room === roomId && n.triggerOnSpawn);
    if (!npc) return;
    const linesKey = hasVisited() ? npc.repeatDialogueKey : npc.dialogueKey;
    const lines = DIALOGUE[linesKey];
    if (!lines || lines.length === 0) return;

    this.controller.setEnabled(false);
    this.objectManager.setSuspended(true);
    this.dock?.setSuspended(true);
    // Track which choice the visitor picked + flag the email-capture
    // branch so the modal opens after the dialogue closes.
    let pendingEmailCapture = false;
    this.time.delayedCall(450, () => {
      this.dialogue.open(lines, {
        onChoice: (choice) => {
          // Log to Supabase events table — free, owned, queryable.
          trackEvent('dialogue_choice', {
            choice_id: choice.id ?? '(unset)',
            choice_label: choice.label ?? '(unset)',
            dialogue: linesKey,
          });
          if (choice.id === 'learn-to-build') pendingEmailCapture = true;
        },
        onComplete: () => {
          this.controller.setEnabled(true);
          this.objectManager.setSuspended(false);
          this.dock?.setSuspended(false);
          if (pendingEmailCapture) {
            // Re-disable everything while the email modal is up
            this.controller.setEnabled(false);
            this.objectManager.setSuspended(true);
            this.dock?.setSuspended(true);
            this.emailCapture.open({
              onClose: () => {
                this.controller.setEnabled(true);
                this.objectManager.setSuspended(false);
                this.dock?.setSuspended(false);
              },
            });
          }
        },
      });
    });
  }

  openModal(obj) {
    this.controller.setEnabled(false);
    this.objectManager.setSuspended(true);
    this.dock?.setSuspended(true);
    this.modal.open(obj, {
      onClose: () => {
        this.controller.setEnabled(true);
        this.objectManager.setSuspended(false);
        this.dock?.setSuspended(false);
      },
    });
  }

  openLink(obj) {
    if (!obj?.url) return;
    this.controller.setEnabled(false);
    this.objectManager.setSuspended(true);
    this.dock?.setSuspended(true);
    this.modal.open(obj, {
      confirm: () => window.open(obj.url, '_blank', 'noopener,noreferrer'),
      onClose: () => {
        this.controller.setEnabled(true);
        this.objectManager.setSuspended(false);
        this.dock?.setSuspended(false);
      },
    });
  }

  // Worktable dispatches here (type: 'dialogue'). Re-runs the intro / repeat-
  // visit dialogue lines so visitors can re-trigger Ian's conversation.
  openDialogue(obj) {
    const linesKey = hasVisited() ? obj.repeatDialogueKey : obj.dialogueKey;
    const lines = DIALOGUE[linesKey];
    if (!lines || lines.length === 0) return;

    this.controller.setEnabled(false);
    this.objectManager.setSuspended(true);
    this.dock?.setSuspended(true);
    this.dialogue.open(lines, {
      onComplete: () => {
        this.controller.setEnabled(true);
        this.objectManager.setSuspended(false);
        this.dock?.setSuspended(false);
      },
    });
  }

  // TV dispatches here (type: 'tvPortfolio'). Opens the streaming-style
  // video portfolio modal — reads VIDEO_PORTFOLIO from world.config.js.
  openTvPortfolio() {
    this.tvPortfolio?.open();
  }

  // v1: no locked-door hints (no doors). showLockedHint preserved in git
  // history for v2+ revival.

  update(time, delta) {
    this.controller?.update(time, delta);
    this._enforceWalls();
    this.objectManager?.update();
    this._updateHintFade(delta);
  }

  // Keeps the drone inside the room's interior. Door tiles are passages — the drone
  // can drift into them so RoomManager can fire the transition. Locked doors stay
  // walls so the drone bumps and the locked-hint shows.
  // Skipped while the controller is disabled (cinematic fly-in, dialogue-driven
  // positioning, room transitions) so tweens and scripted moves aren't clobbered.
  _enforceWalls() {
    if (!this.controller?.enabled) return;
    const room = this.currentRoom;
    if (!room) return;
    const tile = this.tile;
    const r = tile / 2 - 6; // small inset so drone visually clears the wall

    const innerMinX = tile + r;
    const innerMaxX = (room.widthTiles - 1) * tile - r;
    const innerMinY = tile + r;
    const innerMaxY = (room.heightTiles - 1) * tile - r;

    let allowX = false;
    let allowY = false;
    for (const door of room.doors ?? []) {
      const target = ROOMS.find((rr) => rr.id === door.toRoom);
      const isLocked = door.locked || target?.locked;
      if (isLocked) continue; // locked doors collide like walls
      const dxCenter = door.x * tile + tile / 2;
      const dyCenter = door.y * tile + tile / 2;
      const onLeftOrRight = door.x === 0 || door.x === room.widthTiles - 1;
      const onTopOrBottom = door.y === 0 || door.y === room.heightTiles - 1;
      // If drone is aligned with a door's perpendicular axis, allow passage on that axis
      if (onLeftOrRight && Math.abs(this.drone.y - dyCenter) < tile * 0.6) allowX = true;
      if (onTopOrBottom && Math.abs(this.drone.x - dxCenter) < tile * 0.6) allowY = true;
    }

    if (!allowX) this.drone.x = Phaser.Math.Clamp(this.drone.x, innerMinX, innerMaxX);
    if (!allowY) this.drone.y = Phaser.Math.Clamp(this.drone.y, innerMinY, innerMaxY);
  }

  _updateHintFade(delta) {
    // Controls hint is now persistent — never fades. Method kept as a no-op
    // because update() still calls it; cheaper than restructuring the loop.
  }
}
